import React, { useState } from 'react';

const MenuIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ChevronDownIcon = ({ open }) => (
  <svg
    className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
    fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);

  const navItems = [
    {
      label: 'Our School',
      submenu: [
        { label: 'About Us', href: '/about' },
        { label: 'NYSED CSD 10 Learning Technology Grant 2025', href: '/nysed-grant-2025' },
        { label: "Principal's Message", href: '/principal-message' },
        { label: 'TeachHub', href: 'https://teachhub.schools.nyc/Home/Index', external: true },
      ]
    },
    {
      label: 'Our Classes',
      submenu: [
        { label: 'Pre-K', href: '/our-classes#pre-k' },
        { label: 'Kindergarten', href: '/our-classes#kindergarten' },
        { label: '1st Grade', href: '/our-classes#grade-1' },
        { label: '2nd Grade', href: '/our-classes#grade-2' },
        { label: '3rd Grade', href: '/our-classes#grade-3' },
        { label: '4th Grade', href: '/our-classes#grade-4' },
        { label: '5th Grade', href: '/our-classes#grade-5' },
      ]
    },
    {
      label: 'Parent Portal',
      submenu: [
        { label: 'Announcements', href: '/announcements' },
        { label: 'Family Calendar', href: '/calendar' },
        { label: 'Newsletters', href: '/newsletters' },
        { label: 'Technology Resources', href: '/technology-resources' },
        { label: 'Curriculum', href: '/curriculum' },
      ]
    },
    {
      label: 'Student Services',
      submenu: [
        { label: 'Guidance & Counseling', href: '/guidance-counseling' },
        { label: 'Related Services', href: '/related-services' },
        { label: 'Enrichment', href: '/enrichment' },
      ]
    },
    {
      label: 'Contact Us',
      submenu: [
        { label: 'Contact P.S. 85', href: '/contact' },
        { label: 'Out of Classroom', href: '/out-of-classroom' },
        { label: 'Paraprofessionals', href: '/paraprofessionals' },
        { label: 'School Secretaries', href: '/school-secretaries' },
        { label: 'School Aides', href: '/school-aides' },
        { label: 'Family Worker', href: '/family-worker' },
      ]
    },
  ];

  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';

  const toggleSubmenu = (index) => {
    setOpenSubmenu(openSubmenu === index ? null : index);
  };

  return (
    <header
      className="fixed inset-x-0 top-0 z-50 bg-school-blue shadow-lg shadow-black/20"
    >
      <a
        href="#main-content"
        className="sr-only text-school-gold focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:px-4 focus:py-2 focus:bg-school-gold focus:text-school-blue focus:rounded-lg focus:font-bold focus:outline-none focus:ring-2 focus:ring-white"
      >
        Skip to main content
      </a>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <nav className="flex items-center justify-between py-3" aria-label="Primary navigation">

          {/* Logo */}
          <a href="/" className="flex items-center gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-school-gold rounded-lg">
            <img
              src="/assets/images/logo_white.png"
              alt="PS 85 The Great Expectations School"
              className="h-14 w-auto transition-all duration-300"
            />
            <div className="hidden md:flex flex-col text-white">
              <span className="text-lg font-heading font-bold leading-none tracking-tight">PS 85</span>
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/55">Great Expectations</span>
            </div>
          </a>

          <div className="flex items-center gap-2">
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item, index) => (
              <div key={index} className="relative group">
                <button
                  onClick={() => toggleSubmenu(index)}
                  className="flex items-center gap-1.5 text-sm font-semibold text-white/80 hover:text-school-gold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-school-gold rounded px-3 py-2"
                  aria-expanded={openSubmenu === index}
                  aria-haspopup="true"
                >
                  {item.label}
                  <ChevronDownIcon open={openSubmenu === index} />
                </button>

                {/* Dropdown */}
                <div className="absolute top-full right-0 mt-1.5 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 origin-top-right">
                  <div className="rounded-2xl overflow-hidden shadow-2xl shadow-black/30 border border-white/8" style={{ backgroundColor: '#0F2240' }}>
                    {/* Gold top accent line */}
                    <div className="h-0.5 bg-school-gold w-full" />
                    <div className="py-2">
                      {item.submenu.map((subItem, subIndex) => (
                        <a
                          key={subIndex}
                          href={subItem.href}
                          {...(subItem.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                          className="block px-5 py-2.5 text-sm text-white/60 hover:text-school-gold hover:bg-white/5 transition-colors"
                          {...(currentPath === subItem.href ? { 'aria-current': 'page' } : {})}
                        >
                          {subItem.label}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <span id="site-language" className="inline-flex items-center"></span>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-school-gold transition-colors"
            aria-label="Toggle mobile menu"
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
          </div>
        </nav>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden absolute top-full left-0 right-0 shadow-2xl shadow-black/40 max-h-[80vh] overflow-y-auto border-t border-white/8"
          style={{ backgroundColor: '#0F2240' }}
        >
          <div className="px-5 py-6 space-y-1">
            {navItems.map((item, index) => (
              <div key={index} className="border-b border-white/6 last:border-0">
                <button
                  onClick={() => toggleSubmenu(index)}
                  className="flex items-center justify-between w-full text-left text-sm font-semibold text-white/80 hover:text-school-gold py-3.5 transition-colors"
                  aria-expanded={openSubmenu === index}
                  aria-haspopup="true"
                >
                  {item.label}
                  <ChevronDownIcon open={openSubmenu === index} />
                </button>
                {openSubmenu === index && (
                  <div className="pl-4 mb-3 space-y-0.5 border-l-2 border-school-gold/30 ml-1">
                    {item.submenu.map((subItem, subIndex) => (
                      <a
                        key={subIndex}
                        href={subItem.href}
                        {...(subItem.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        className="block text-sm text-white/70 hover:text-school-gold py-2 transition-colors"
                        {...(currentPath === subItem.href ? { 'aria-current': 'page' } : {})}
                      >
                        {subItem.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
